import { GLOBAL_FALLBACK_UUID, Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { electronModule } from "@/modules/electron";
import { uuidModule } from "@/modules/uuid";
import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
} from "@/domains/errors";
import {
  PreparedRecording,
  PreparedRecordingController,
  RecallUploadCredentials,
} from "@/store/audio/recording/prepared-recording-controller/types";
import { DateTime } from "luxon";

interface RecallSdkUploadResponse {
  upload_token: string;
  sdk_upload_id?: string;
}

export class DesktopPreparedRecordingController implements PreparedRecordingController {
  prepareRecording({
    customVocabulary,
    audioRecordingId,
  }: {
    customVocabulary: string[] | null;
    audioRecordingId: Uuid;
  }): PreparedRecording {
    return {
      audioRecordingId,
      customVocabulary,
      windowId: this.fetchWindowId(),
      recallUploadCredentials: this.fetchRecallUploadCredentials(
        audioRecordingId,
        customVocabulary
      ),
      // After 3 hours, @recallai/desktop-sdk startRecording needs to do a network
      // request to confirm it, taking 0.5~1 second.
      expiresAt: DateTime.local().plus({ hours: 2, minutes: 30 }),
    };
  }

  private async fetchWindowId(): Promise<string> {
    try {
      const { windowId } = await electronModule.recall.prepareDesktopRecording();
      return windowId;
    } catch (error) {
      throw new AudioRecordingError({
        source: AudioRecordingErrorSource.RecallDesktop,
        stage: AudioRecordingErrorStage.PrepareWindow,
        message: error instanceof Error ? error.message : String(error),
        originalError: error instanceof Error ? error : undefined,
        details: {
          action: "prepareDesktopRecording",
        },
      });
    }
  }

  private async fetchRecallUploadCredentials(
    audioRecordingId: Uuid,
    customVocabulary: string[] | null
  ): Promise<RecallUploadCredentials> {
    const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });
    let response;
    try {
      response = await api.post("/v2/voice/recall_sdk_upload", {
        body: {
          // This is not being used by the backend.
          note_id: GLOBAL_FALLBACK_UUID,
          captured_audio_id: capturedAudioId,
          recording_config: {
            transcript: {
              provider: {
                assembly_ai_v3_streaming: {
                  format_turns: true,

                  keyterms_prompt: customVocabulary?.length
                    ? JSON.stringify(customVocabulary)
                    : null,
                },
              },
            },
            realtime_endpoints: [
              {
                type: "desktop-sdk-callback",
                events: [
                  "participant_events.join",
                  "transcript.data",
                  "transcript.partial_data",
                  "audio_mixed_raw.data",
                ],
              },
            ],
            // System audio only
            video_mixed_mp4: null,
            audio_mixed_mp3: {},
          },
        },
      });
    } catch (error) {
      throw new AudioRecordingError({
        source: AudioRecordingErrorSource.RecallDesktop,
        stage: AudioRecordingErrorStage.UploadCredentials,
        message: error instanceof Error ? error.message : String(error),
        originalError: error instanceof Error ? error : undefined,
        details: {
          endpoint: "/v2/voice/recall_sdk_upload",
        },
      });
    }
    const data = response.data as RecallSdkUploadResponse | undefined;
    if (!data?.upload_token) {
      throw new AudioRecordingError({
        source: AudioRecordingErrorSource.RecallDesktop,
        stage: AudioRecordingErrorStage.UploadCredentials,
        message: "Recall upload token missing in response.",
        details: {
          endpoint: "/v2/voice/recall_sdk_upload",
        },
      });
    }
    return {
      uploadToken: data.upload_token,
      sdkUploadId: data.sdk_upload_id ?? null,
    };
  }
}
